import { database } from '../config/backend';
import { ref, push, set as rtdbSet, get as rtdbGet, serverTimestamp } from './phpDataAdapter';

// URL base do Hostinger para links de PDF
const HOSTINGER_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nutrisync.dgapp.com.br';

export interface PDFDocument {
  id?: string;
  patientId: string;
  nutritionistId: string;
  type: 'measurements' | 'mealPlan' | 'examRequest' | 'assessment' | 'manipulated';
  fileName: string;
  pdfData: string; // Base64 encoded PDF
  message?: string; // Mensagem personalizada
  createdAt: string;
  sentAt?: string;
  viewedAt?: string;
  status: 'pending' | 'sent' | 'viewed';
}

/**
 * Helper para criar um ID de conversa consistente
 */
function buildConversationId(uid1: string, uid2: string): string {
  if (!uid1 || !uid2) {
    return '';
  }
  return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
}

/**
 * Obtém ou cria uma conversa entre dois usuários
 */
async function getOrCreateConversation(user1Id: string, user2Id: string): Promise<string> {
  if (!user1Id || !user2Id) {
    throw new Error('IDs de usuário inválidos');
  }
  
  const conversationId = buildConversationId(user1Id, user2Id);
  if (!conversationId) {
    throw new Error('Não foi possível criar ID de conversa');
  }
  
  try {
    const conversationRef = ref(database, `conversations/${conversationId}`);
    const snapshot = await rtdbGet(conversationRef);

    if (snapshot.exists()) {
      return conversationId;
    } else {
      // Criar nova conversa
      await rtdbSet(conversationRef, {
        participants: [user1Id, user2Id],
        createdAt: serverTimestamp(),
        lastMessage: ''
      });
      return conversationId;
    }
  } catch (error) {
    console.error('Erro ao criar/buscar conversa:', error);
    throw error;
  }
}

/**
 * Envia PDF para o paciente via chat/notificação
 */
export async function sendPDFToPatient(
  patientId: string,
  nutritionistId: string,
  type: PDFDocument['type'],
  fileName: string,
  pdfBlob: Blob,
  message?: string,
  sendToChat: boolean = true
): Promise<string> {
  try {
    // Converter PDF para base64
    const base64 = await blobToBase64(pdfBlob);

    // Criar documento PDF
    const pdfDoc: Omit<PDFDocument, 'id'> = {
      patientId,
      nutritionistId,
      type,
      fileName,
      pdfData: base64,
      ...(message && message.trim() ? { message: message.trim() } : {}),
      createdAt: new Date().toISOString(),
      status: 'pending'
    };

    // Salvar no banco de dados
    const pdfRef = push(ref(database, `pdfDocuments`));
    await rtdbSet(pdfRef, pdfDoc);

    // Se sendToChat for true, enviar mensagem no chat
    if (sendToChat) {
      // Obter ou criar conversa entre paciente e nutricionista
      const conversationId = await getOrCreateConversation(patientId, nutritionistId);
      
      if (!conversationId) {
        throw new Error('Não foi possível criar ou obter a conversa');
      }

      // Criar mensagem na conversa
      const messageRef = push(ref(database, `messages/${conversationId}`));
      // Sempre usar URL do Hostinger, pois os PDFs estão salvos no servidor
      const pdfUrl = `${HOSTINGER_BASE_URL}/pdf/${pdfRef.key}`;
      const messageContent = message 
        ? `📄 ${message}\n\nDocumento: ${fileName}\n\n🔗 Acessar: ${pdfUrl}`
        : `📄 Novo documento disponível: ${fileName}\n\n🔗 Acessar: ${pdfUrl}`;
      
      await rtdbSet(messageRef, {
        text: messageContent,
        senderId: nutritionistId,
        senderName: 'Nutricionista',
        type: 'pdf',
        pdfId: pdfRef.key,
        pdfUrl: pdfUrl,
        pdfFileName: fileName,
        createdAt: serverTimestamp(),
        read: false
      });

      // Atualizar última mensagem na conversa
      await rtdbSet(ref(database, `conversations/${conversationId}/lastMessage`), messageContent);
      await rtdbSet(ref(database, `conversations/${conversationId}/lastMessageTimestamp`), serverTimestamp());
    }

    // Marcar como enviado
    await rtdbSet(ref(database, `pdfDocuments/${pdfRef.key}/sentAt`), new Date().toISOString());
    await rtdbSet(ref(database, `pdfDocuments/${pdfRef.key}/status`), 'sent');

    return pdfRef.key as string;
  } catch (error) {
    console.error('Erro ao enviar PDF:', error);
    throw error;
  }
}

/**
 * Marca PDF como visualizado pelo paciente
 */
export async function markPDFAsViewed(pdfId: string): Promise<void> {
  try {
    await rtdbSet(ref(database, `pdfDocuments/${pdfId}/viewedAt`), new Date().toISOString());
    await rtdbSet(ref(database, `pdfDocuments/${pdfId}/status`), 'viewed');
  } catch (error) {
    console.error('Erro ao marcar PDF como visualizado:', error);
  }
}

/**
 * Gera preview do PDF em base64 para exibição
 */
export async function generatePDFPreview(pdfBlob: Blob): Promise<string> {
  return await blobToBase64(pdfBlob);
}

/**
 * Converte Blob para Base64
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = (reader.result as string).split(',')[1];
      resolve(base64String);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Gera PDF como Blob a partir de jsPDF
 */
export async function generatePDFBlob(pdfDoc: any): Promise<Blob> {
  try {
    // jsPDF.output('blob') retorna um Blob diretamente
    const blob = pdfDoc.output('blob');
    return blob;
  } catch (error) {
    console.error('Erro ao gerar blob do PDF:', error);
    throw new Error('Não foi possível gerar o PDF');
  }
}

/**
 * Envia PDF de medições para paciente
 */
export async function sendMeasurementsPDFToPatient(
  patientId: string,
  nutritionistId: string,
  patientName: string,
  measurements: any[]
): Promise<void> {
  try {
    // Criar PDF temporário
    const tempDiv = document.createElement('div');
    tempDiv.style.display = 'none';
    document.body.appendChild(tempDiv);

    // Gerar PDF usando jsPDF diretamente
    const jsPDF = (await import('jspdf')).default;
    const autoTable = (await import('jspdf-autotable')).default;

    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Relatório de Medições Antropométricas', 14, 20);
    doc.setFontSize(12);
    doc.text(`Paciente: ${patientName}`, 14, 28);
    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 14, 34);

    const tableData = measurements.map(m => [
      new Date(m.timestamp).toLocaleDateString('pt-BR'),
      m.weight ? `${m.weight.toFixed(1)} kg` : '-',
      m.height ? `${m.height.toFixed(1)} cm` : '-',
      m.bodyFatPercentage ? `${m.bodyFatPercentage.toFixed(1)}%` : '-',
    ]);

    autoTable(doc, {
      startY: 40,
      head: [['Data', 'Peso', 'Altura', '% Gordura']],
      body: tableData,
    });

    const blob = await generatePDFBlob(doc);
    const fileName = `medicoes_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

    await sendPDFToPatient(patientId, nutritionistId, 'measurements', fileName, blob);
    
    document.body.removeChild(tempDiv);
    alert('PDF enviado para o paciente com sucesso!');
  } catch (error) {
    console.error('Erro ao enviar PDF de medições:', error);
    alert('Erro ao enviar PDF. Tente novamente.');
  }
}

